import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, alpha } from '@material-ui/core/styles';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
} from '@material-ui/core';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { useTranslation } from 'react-i18next';
import { decodeToken, userRoles } from 'utils';
import {
  clearNewNotifications,
  fetchNotifications,
  fetchUnseenNotifications,
  markNotificationAsSeen,
  markNotificationsAsSeen,
} from 'redux/actions/applicationAction';
import { useHistory } from 'react-router-dom';
import { MARK_NOTIFICATIONS_AS_SEEN } from 'redux/actions/actiontype';

const useStyles = makeStyles((theme) => ({
  notificationPopover: {
    width: 400,
    maxHeight: 500,
    overflow: 'auto',
  },
  notificationHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#8DC63F',
    color: '#fff',
  },
  notificationList: {
    padding: 0,
  },
  notificationItem: {
    padding: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  unseenNotification: {
    backgroundColor: alpha(theme.palette.warning.light, 0.3),
    fontWeight: 'bold',
  },
  seenNotification: {
    backgroundColor: 'transparent',
  },
  noNotifications: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  notificationTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  recipientEmail: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  nbrMessages: {
    fontSize: '0.9rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

const Notifications = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [anchorEl, setAnchorEl] = useState(null);
  const userData = decodeToken();
  const userId =
      userData?.userRole?.id === 5 ? userData?.id : userData?.applicant?.id;

  const { notifications, newNotificationsCount } = useSelector(
    (state) => state.notifications
  );
  // console.log(`notifications`, notifications);

  useEffect(() => {
    if (userId) {
      dispatch(fetchNotifications(userId));
      dispatch(fetchUnseenNotifications(userId));
    }
  }, [dispatch]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
 
    if (!userId) return;
  
    // Applicant sees all notifications as seen at once
    // if (userData?.userRole?.id !== 5) {
    //   dispatch({ type: MARK_NOTIFICATIONS_AS_SEEN });
    //   dispatch(markNotificationsAsSeen(userId));
    // }
  };

  const handleClose = () => {
    setAnchorEl(null);
    
    
    if (userId) {
      dispatch(fetchUnseenNotifications(userId));
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const handleNotificationClick = (loanApplicationId) => {
      if (userData?.userRole?.id !== 5) {
        dispatch({ type: MARK_NOTIFICATIONS_AS_SEEN });
        dispatch(markNotificationsAsSeen(userId));
      }
  
    if (userData?.userRole?.id === 5) {
      const allInGroup = notifications.filter(
        (notif) => notif.loanApplicationId === loanApplicationId && !notif.seen
      );
  
      const ids = allInGroup.map((n) => n.id);
  
      dispatch({ type: 'MARK_NOTIFICATION_SEEN_LOCALLY', payload: ids });
  
      ids.forEach((notificationId) => {
        dispatch(markNotificationAsSeen(userId, notificationId));
      });
  
      history.replace('/loan/applications');
      setTimeout(() => {
        history.push(`/loan/applications/${loanApplicationId}`);
      }, 0);
    }
  
    handleClose();
  };

  const groupedNotifications = notifications.reduce((acc, notif) => {
    const { loanApplicationId } = notif;
    if (!acc[loanApplicationId]) {
      acc[loanApplicationId] = [];
    }
    acc[loanApplicationId].push(notif);
    return acc;
  }, {});

  const groupedList = Object.entries(groupedNotifications).map(
    ([loanApplicationId, notifs]) => {
      const sorted = notifs.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      const latest = sorted[0];
      return {
        ...latest,
        count: notifs.length,
      };
    }
  ).sort((a, b) => {
    if (a.seen !== b.seen) return a.seen ? 1 : -1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  return (
    <>
      <IconButton
        aria-label="show notifications"
        color="inherit"
        onClick={handleClick}
      >
        <Badge badgeContent={newNotificationsCount} color="secondary">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div className={classes.notificationPopover}>
          <div className={classes.notificationHeader}>
            <Typography variant="h6">{t('Notifications')}</Typography>
          </div>
          {groupedList.length > 0 ? (
            <List className={classes.notificationList}>
              {groupedList.map((notification) => {
                const notificationClass = notification.seen
                  ? classes.seenNotification
                  : classes.unseenNotification;

                return (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      button
                      className={`${classes.notificationItem} ${notificationClass}`}
                      onClick={() =>
                        handleNotificationClick(notification?.loanApplicationId)
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography className={classes.notificationTitle}>
                            {notification.notificationTitle} (
                            {notification.count})
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="body2">
                              {notification.message}
                            </Typography>
                            <Typography className={classes.nbrMessages}>
                              {notification.count} messages
                            </Typography>
                            <Typography className={classes.recipientEmail}>
                              From: {notification.sentFrom || userRoles.RHA_OFFICER}
                            </Typography>
                            <Typography className={classes.timestamp}>
                              {formatDate(notification.createdAt)}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <div className={classes.noNotifications}>
              <Typography>{t('no_notifications')}</Typography>
            </div>
          )}
        </div>
      </Popover>
    </>
  );
};

export default Notifications;
